/**
 * Application constants for trAInedup.ai
 * Centralized configuration and constant values
 */

import { type SupportedCurrency, detectCurrency } from './currency';

// Re-export types for convenience
export { UserRole } from '../types';

// Google Analytics Configuration
// GA4 Measurement ID (G-XXXXXXXXX format)
export const GA_TRACKING_ID = process.env.REACT_APP_FIREBASE_MEASUREMENT_ID || 'G-CKN1KSKL7B';

// Multi-currency pricing data
// Each tier has pricing for GBP, EUR, and USD

const env = process.env;

export const PRICING_DATA = {
  standard: {
    GBP: { amount: 1299, display: '£12.99', priceId: env.REACT_APP_STRIPE_STANDARD_PRICE_ID || '' },
    EUR: { amount: 1499, display: '€14.99', priceId: env.REACT_APP_STRIPE_STANDARD_PRICE_ID_EUR || '' },
    USD: { amount: 1799, display: '$17.99', priceId: env.REACT_APP_STRIPE_STANDARD_PRICE_ID_USD || '' },
  },
  premium: {
    GBP: { amount: 4999, display: '£49.99', priceId: env.REACT_APP_STRIPE_PREMIUM_PRICE_ID || '' },
    EUR: { amount: 5999, display: '€59.99', priceId: env.REACT_APP_STRIPE_PREMIUM_PRICE_ID_EUR || '' },
    USD: { amount: 6999, display: '$69.99', priceId: env.REACT_APP_STRIPE_PREMIUM_PRICE_ID_USD || '' },
  },
  twelve_month_package: {
    GBP: { amount: 4999, display: '£49.99', priceId: env.REACT_APP_STRIPE_TWELVE_MONTH_PACKAGE_PRICE_ID || '' },
    EUR: { amount: 5999, display: '€59.99', priceId: env.REACT_APP_STRIPE_TWELVE_MONTH_PACKAGE_PRICE_ID_EUR || '' },
    USD: { amount: 6999, display: '$69.99', priceId: env.REACT_APP_STRIPE_TWELVE_MONTH_PACKAGE_PRICE_ID_USD || '' },
  },
} as const;

export type PricingTier = keyof typeof PRICING_DATA;

/** Get pricing for a specific tier in the detected (or provided) currency */
export function getPricing(tier: PricingTier, currency?: SupportedCurrency) {
  const c = currency || detectCurrency();
  return PRICING_DATA[tier][c];
}

// Backward-compatible SUBSCRIPTION_PRICES — resolves to the detected currency
// so all existing consumers auto-get the correct currency display and price ID
export const SUBSCRIPTION_PRICES = (() => {
  const c = detectCurrency();
  return {
    standard: {
      monthly: PRICING_DATA.standard[c].amount,
      display: PRICING_DATA.standard[c].display,
      stripePriceId: PRICING_DATA.standard[c].priceId,
    },
    premium: {
      monthly: PRICING_DATA.premium[c].amount,
      display: PRICING_DATA.premium[c].display,
      stripePriceId: PRICING_DATA.premium[c].priceId,
    },
    twelve_month_package: {
      oneTime: PRICING_DATA.twelve_month_package[c].amount,
      display: PRICING_DATA.twelve_month_package[c].display,
      stripePriceId: PRICING_DATA.twelve_month_package[c].priceId,
    },
  };
})();

// Subscription tier features
export const TIER_FEATURES = {
  standard: [
    'Skills Assessment',
    'Personalized Learning Paths',
    'AI Training Packs',
    'Community access',
    'trAInedup.ai support',
  ],
  premium: [
    'Everything in Standard',
    'Expert consultations',
    'Ability to book additional consultations',
  ],
  twelve_month_package: [
    'Skills Assessment',
    'Personalized Learning Paths',
    'AI Training Packs',
    'Community access',
    'trAInedup.ai support',
    '12 months full access',
    'One-time payment - No recurring fees',
  ],
  corporate: [
    'Training packs designed for your organisation',
    'Flexible delivery: workshops, self-paced, or blended',
    'Expert consultations and ongoing support',
    'Scalable solutions for teams of any size',
  ],
} as const;

// Skills assessment configuration
export const SKILLS_ASSESSMENT = {
  competencies: [
    'promptEngineering',
    'criticalEvaluation',
    'dataInterpretation',
    'toolUsage',
    'ethicalConsiderations',
  ],
  maxScore: 5,
  minScore: 1,
  questionsPerCompetency: 3,
} as const;

// Course difficulty levels
export const COURSE_LEVELS = {
  1: 'Beginner',
  2: 'Elementary',
  3: 'Intermediate',
  4: 'Advanced',
  5: 'Expert',
} as const;

// Platform limits and constraints
export const PLATFORM_LIMITS = {
  maxFileUploadSize: 10 * 1024 * 1024, // 10MB
  maxMessageLength: 1000,
  maxCommentLength: 500,
  maxCourseTitleLength: 100,
  maxCourseDescriptionLength: 500,
  maxDisplayNameLength: 50,
} as const;

// Firebase collection names
export const COLLECTIONS = {
  users: 'users',
  courses: 'courses',
  userProgress: 'user_progress',
  learningPaths: 'learningPaths',
  comments: 'comments',
  conversations: 'conversations',
  messages: 'messages',
  experts: 'experts',
  consultations: 'consultations',
  subscriptions: 'subscriptions',
  referrals: 'referrals',
  notifications: 'notifications',
  metrics: 'metrics',
} as const;

// API endpoints
export const API_ENDPOINTS = {
  createSubscription: '/api/create-subscription',
  cancelSubscription: '/api/cancel-subscription',
  createPortalSession: '/api/create-portal-session',
  webhooks: '/api/webhooks',
  assessSkills: '/api/assess-skills',
  generateLearningPath: '/api/generate-learning-path',
} as const;

// External service URLs
export const EXTERNAL_SERVICES = {
  chatbaseWidget: process.env.REACT_APP_CHATBASE_WIDGET_ID || '',
  stripePublishableKey: process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '',
} as const;

// Navigation routes
export const ROUTES = {
  home: '/',
  landingPage: '/landing-page',
  about: '/about',
  pricing: '/pricing',
  contact: '/contact',
  login: '/login',
  register: '/register',
  forgotPassword: '/forgot-password',
  resetPassword: '/reset-password',
  dashboard: '/dashboard',
  dashboardCourses: '/dashboard/courses',
  dashboardCoursesInProgress: '/dashboard/courses-in-progress',
  dashboardCoursesCompleted: '/dashboard/courses-completed',
  dashboardProgress: '/dashboard/progress',
  profile: '/profile',
  courses: '/courses',
  courseCatalog: '/course-catalog',
  blog: '/blog',
  blogPost: '/blog/:slug',
  course: '/courses/:id',
  certificate: '/certificate/:courseId',
  // Deprecated: kept for backward compatibility; points to skills assessment
  assessment: '/skills-assessment',
  skillsAssessment: '/skills-assessment',
  learningPlan: '/learning-plan',
  learningJourney: '/learning-journey',
  messages: '/messages',
  experts: '/experts',
  admin: '/admin',
  adminUsers: '/dashboard/admin/users',
  adminAnalytics: '/dashboard/admin/analytics',
  adminAffiliates: '/dashboard/admin/affiliates',
  adminActivationCodes: '/dashboard/admin/activation-codes',
  adminActivationRedemptions: '/dashboard/admin/activation-redemptions',
  adminSettings: '/dashboard/admin/settings',
  adminCourses: '/dashboard/admin/courses',
  adminCoursesCreate: '/dashboard/admin/courses/create',
  adminCourseEdit: '/dashboard/admin/courses/:id/edit',
  adminEmails: '/dashboard/admin/emails',
  adminEmailFlowVisualization: '/dashboard/admin/emails/flow',
  adminEmailFlowChange: '/dashboard/admin/emails/flow/change',
  adminSupport: '/dashboard/admin/support',
  adminBlog: '/dashboard/admin/blog',
  support: '/dashboard/support',
  billing: '/billing',
  community: '/community',
  corporateRequest: '/corporate-request',
  unauthorized: '/unauthorized',
  verifyEmail: '/verify-email',
  emailVerificationSent: '/email-verification-sent',
  emailVerificationRequired: '/email-verification-required',
  aiReadiness: '/ai-readiness',
} as const;

// Local storage keys
export const STORAGE_KEYS = {
  authToken: 'trainedup_auth_token',
  userPreferences: 'trainedup_user_preferences',
  onboardingProgress: 'trainedup_onboarding_progress',
} as const;

// Error messages
export const ERROR_MESSAGES = {
  generic: 'Something went wrong. Please try again.',
  networkError: 'Network error. Please check your connection.',
  authRequired: 'Please log in to access this feature.',
  insufficientPermissions: 'You do not have permission to perform this action.',
  subscriptionRequired: 'This feature requires an active subscription.',
  upgradeRequired: 'Please upgrade your subscription to access this feature.',
  invalidInput: 'Please check your input and try again.',
  fileTooBig: 'File size exceeds the maximum limit.',
  unsupportedFileType: 'This file type is not supported.',
} as const;

// Success messages
export const SUCCESS_MESSAGES = {
  profileUpdated: 'Profile updated successfully!',
  courseCompleted: 'Congratulations! Course completed.',
  messagesSent: 'Message sent successfully.',
  subscriptionUpdated: 'Subscription updated successfully.',
  accountCreated: 'Account created successfully! Welcome to trAInedup.ai',
} as const;

// Brand voice guidelines for UI text
export const BRAND_VOICE = {
  approachable: {
    welcomeMessage: "We’ll help you understand what AI can do for you and your work – no previous AI experience needed.",
    supportMessage: "You don’t need to be an expert – you just need tailored training that matches your goals.",
  },
  supportive: {
    supportMessage: "Even if you've never used AI before, we'll guide you step by step.",
    encouragementMessage: "Don't worry — we've got you covered every step of the way.",
  },
  empowering: {
    motivationalMessage: "You don't need to be an expert — you just need the right tools for your goals.",
    progressMessage: "Look how far you've come! Keep building your AI skills at your own pace.",
  },
  practical: {
    actionableMessage: "Here are 3 ways you can start using AI this week to save time.",
    clarityMessage: "Let's break this down into simple, actionable steps.",
  },
} as const;

// Responsive breakpoints (matching Tailwind defaults)
export const BREAKPOINTS = {
  sm: '640px',
  md: '768px',
  lg: '1024px',
  xl: '1280px',
  '2xl': '1536px',
} as const;

// Feature flags (toggle via .env):
// - REACT_APP_SHOW_ADMIN_SEED_BUTTON: 'true' | 'false' (default: true in dev, false in prod)
// - REACT_APP_AUTO_SAMPLE_ANALYTICS: 'true' | 'false' (default: true in dev, false in prod)
export const FEATURE_FLAGS = {
  showAdminSeedButton:
    (process.env.REACT_APP_SHOW_ADMIN_SEED_BUTTON === 'true') ||
    (process.env.NODE_ENV !== 'production' && process.env.REACT_APP_SHOW_ADMIN_SEED_BUTTON !== 'false'),
  autoSampleAnalyticsOnError:
    (process.env.REACT_APP_AUTO_SAMPLE_ANALYTICS === 'true') ||
    (process.env.NODE_ENV !== 'production' && process.env.REACT_APP_AUTO_SAMPLE_ANALYTICS !== 'false'),
} as const;

